// Ported from dev-scrips-pm-react src/billing/components/invoice-services-table.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { useEffect, useState } from 'react';
import { BadgeCheck, Pencil, Plus, X } from 'lucide-react';
import { BridgeButton as Button } from '@/billing/bridge';
import {
  isSelfPay as isSelfPayType,
  isInsurance as isInsuranceType,
} from '@/billing/lib/sponsor-type';
import {
  BridgeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import { DateRangeField } from '@/billing/bridge';
import {
  ServiceDatePicker,
  formatServiceDate,
} from '@/billing/components/service-date-picker';
import type { InvoiceService } from '@/shared/types/billing';
import {
  effectiveDiscountPercent,
  formatDiscountPercent,
  hasMemberDiscount,
} from '@/billing/lib/member-discount';

import { formatMoney } from '@/shared/lib/money';
import { LINE_CHIP_CLASS } from '@/billing/lib/line-chip';
import {
  isOrderLine,
  needsKeepOrDrop,
  type InvoiceServiceOrderInfo,
} from '@/billing/lib/pending-orders';
import {
  OrderSourceMarker,
  RemovedOrderActions,
  RevertToPendingButton,
} from '@/billing/components/invoice-service-order-source';
import { BridgeTooltipTrigger as TooltipTrigger, Tooltip, TooltipContent } from '@/billing/bridge';
import { ROW_ICON_BUTTON } from '@/billing/bridge';
/**
 * Services table for the appointment / patient **billing** invoice — Flutter
 * `invoice_services_widget.dart` (the `InvoiceBillingScreen` table), distinct
 * from the claim-review `services-table.tsx` (Mark-Reviewed claim columns).
 *
 * Columns (Flutter, order): # · SERVICES · QTY · GROSS · Disc. · NET ·
 * PATIENT SHARE, plus sponsor-conditional SPONSOR SHARE (insurance),
 * COVERAGE (non-Self-pay), SERVICE DATE (non-Self-pay), AUTH CODE (insurance),
 * VALIDITY PERIOD (insurance). Self-pay shows only the base columns.
 *
 * Editable on Draft (`editable`, = Flutter `enableInvoiceEdit` = invoice status
 * Draft). When editable the header shows a "+" Add affordance, and per row:
 *   - QTY becomes an input                        → `onChangeQty`
 *   - COVERAGE becomes a dropdown                  → `onChangeCoverage`
 *   - SERVICE DATE becomes a date picker           → `onChangeServiceDate`
 *   - AUTH CODE becomes an input                   → `onChangeAuthCode`
 *   - VALIDITY PERIOD becomes a date-range picker  → `onChangeValidity`
 *   - a remove (×) appears                         → `onRemoveService`
 * All flow back through `PUT /Billing/Invoice/{id}` (`useAddRemoveService`,
 * owned by the host page) which recomputes + persists, then refetches.
 * Read-only otherwise (Issued / Cancelled / non-Draft).
 *
 * The discount pencil opens the per-service discount editor — Flutter shows it
 * on **Excluded** rows only for insurance/corporate, and always for Self-pay.
 */
interface Props {
  services: InvoiceService[];
  /** Server field `invoice.sponsorType`. */
  sponsorType?: string;
  currency?: string;
  /**
   * When provided, eligible rows show a pencil (edit-discount) icon in the
   * actions column that opens the per-service discount editor — matches Flutter
   * `invoice_billing_screen` (the pencil beside the delete). Omit for a fully
   * read-only table (e.g. cancelled invoices).
   */
  onEditDiscount?: (service: InvoiceService) => void;
  /** Draft-only editing affordances (Flutter `enableInvoiceEdit`). */
  editable?: boolean;
  /** A recalc is in flight — disable edit controls. */
  busy?: boolean;
  onAddService?: () => void;
  onRemoveService?: (serviceId: string) => void;
  onChangeQty?: (serviceId: string, quantity: number) => void;
  /** Coverage flip (insurance/corporate) → included / excluded / needsApproval. */
  onChangeCoverage?: (serviceId: string, coverage: Coverage) => void;
  /** Service date (insurance/corporate) — emits `"YYYY-MM-DD"`. */
  onChangeServiceDate?: (serviceId: string, yyyyMmDd: string) => void;
  /** Auth code (insurance). */
  onChangeAuthCode?: (serviceId: string, authCode: string) => void;
  /** Validity window (insurance) — emits `"YYYY-MM-DD"` start/end (or undefined). */
  onChangeValidity?: (
    serviceId: string,
    startYmd: string | undefined,
    endYmd: string | undefined,
  ) => void;
  /**
   * Order to Invoice (PROD-2592), per `sourceOrderKey`, for lines with
   * `billableSource === 'Manual'`. Drives the "Added" time and the
   * "Order removed from plan" flag. Lines added directly are untouched.
   */
  orderLines?: Readonly<Record<string, InvoiceServiceOrderInfo>>;
  /** Revert to pending, and Drop on a removed-from-plan line (`.../Orders/revert`). Draft only. */
  onRevertOrderLine?: (sourceOrderKey: string) => void;
  /** Keep a removed-from-plan line (UI only). */
  onKeepOrderLine?: (sourceOrderKey: string) => void;
}

const COVERAGE_INCLUDED = 'Included';
const COVERAGE_EXCLUDED = 'Excluded';
const COVERAGE_NEEDS_APPROVAL = 'Needs Approval';
type Coverage =
  | typeof COVERAGE_INCLUDED
  | typeof COVERAGE_EXCLUDED
  | typeof COVERAGE_NEEDS_APPROVAL;

function fmt(n?: number | null): string {
  if (n == null) return '0.00';
  return formatMoney(n);
}

/**
 * What the patient's membership actually took off this line: the list fee for
 * the quantity billed, less what was charged — and that saving expressed as a
 * percentage of the list total (PROD-2523).
 *
 * The saving is the number the desk and the patient care about, and the only
 * one the row does not already show. It is derived rather than stored because
 * `listFeeBeforeMemberPrice` is a PER-UNIT fee while `grossAmount` is the whole
 * line, so a two-unit line needs the multiplication.
 *
 * `grossAmount` is deliberately the comparison, not `netAmount`: gross is
 * before any typed line discount, so this reports what the MEMBERSHIP did and
 * does not absorb a discount someone keyed in afterwards.
 *
 * Returns null when there is nothing honest to claim - no recorded list fee, or
 * a member price that matches or exceeds it. A plan may price a service at its
 * list fee (the dev catalog has one), and "saves 0.00" is noise; the chip still
 * says "Member price", because a membership setting the price is true whether
 * or not it moved the number.
 *
 * ## `percent` is a fact about THIS LINE, not a claim about which rung
 *
 * The derived percentage is what the line's own numbers work out to, true
 * whichever rung priced it (a per-service member price of 270 against a 300
 * list fee reads "10% off" too — correctly). WHICH rung priced it is not
 * derived here: Billing records it (PROD-2525) and {@link memberChipOf} reads
 * it. This derived number is now only the fallback for a line that does not
 * carry that record.
 */
function memberSavingOf(s: InvoiceService): { saved: number; percent: number | null } | null {
  const listPerUnit = s.listFeeBeforeMemberPrice;
  if (typeof listPerUnit !== 'number') return null;
  const charged = s.grossAmount ?? s.fee;
  if (typeof charged !== 'number') return null;
  const listTotal = listPerUnit * (s.quantity ?? 1);
  const saved = listTotal - charged;
  if (saved <= 0) return null;
  return { saved, percent: effectiveDiscountPercent(saved, listTotal) };
}

/**
 * What the member chip says (PROD-2525): WHICH rung priced the line, when
 * Billing recorded it, plus the saving.
 *
 * - `memberDiscountPercentApplied` present → rung 3, the plan's percentage:
 *   "Member discount 10% · saves 30.00". The percentage is the one Billing
 *   priced the line with, not one derived from rounded fees, so the chip does
 *   not repeat it as "(10% off)".
 * - member-priced but the field is null → "Member price · saves 150.00 (50%
 *   off)", PROD-2523's wording, unchanged. That is right for a rung-2 line (an
 *   explicit member price), and it is the honest fallback for an OLD line
 *   priced by the percentage before Billing recorded the rung: a null there
 *   means "not recorded", not "rung 2", and the effective percentage is still a
 *   true statement about that line.
 *
 * Never inferred from the patient's current plan: the plan may have been
 * repriced since the invoice was, and an old invoice must not report today's
 * benefit.
 */
function memberChipOf(s: InvoiceService): { label: string; savingText: string | null } {
  const saving = memberSavingOf(s);
  const recorded = s.memberDiscountPercentApplied;
  if (hasMemberDiscount(recorded)) {
    return {
      label: `Member discount ${formatDiscountPercent(recorded)}%`,
      savingText: saving !== null ? `· saves ${fmt(saving.saved)}` : null,
    };
  }
  return {
    label: 'Member price',
    savingText:
      saving !== null
        ? `· saves ${fmt(saving.saved)}` +
          (saving.percent !== null ? ` (${formatDiscountPercent(saving.percent)}% off)` : '')
        : null,
  };
}

/**
 * Same chip as the package/membership markings on the patient's receipts list,
 * so one visual language covers every place a membership touches money.
 */
const MEMBER_PRICE_CHIP_CLASS = LINE_CHIP_CLASS;

/**
 * Coverage badge — mirrors Flutter `PatientInvoiceService.selectedCoverageOption()`:
 * Included if `serviceIncluded`, Needs Approval if `serviceNeedsApproval`, else
 * Excluded (the fallback). Reads the real wire keys `serviceIncluded` /
 * `serviceNeedsApproval` (the `isExcluded` / `isApproval` aliases were never on
 * the wire).
 */
function inferCoverage(s: InvoiceService): Coverage {
  if (s.serviceIncluded) return COVERAGE_INCLUDED;
  if (s.serviceNeedsApproval) return COVERAGE_NEEDS_APPROVAL;
  return COVERAGE_EXCLUDED;
}

/** First 10 chars of a no-Z ISO date → `"YYYY-MM-DD"` (empty if unset/sentinel). */
function toYmd(iso?: string | null): string {
  if (!iso || /^0001-01-01/.test(iso)) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : '';
}

/** Read-only display for a service date (`"24 Jul 2026"`). */
function fmtDateDisplay(iso?: string | null): string {
  const ymd = toYmd(iso);
  if (!ymd) return '—';
  const [y, mo, d] = ymd.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Read-only display for a validity window. */
function fmtValidityDisplay(start?: string | null, end?: string | null): string {
  const s = toYmd(start);
  const e = toYmd(end);
  if (!s && !e) return '—';
  if (s && e) return `${fmtDateDisplay(start)} – ${fmtDateDisplay(end)}`;
  return fmtDateDisplay(end ?? start);
}

export function InvoiceServicesTable({
  services,
  sponsorType,
  currency = 'AED',
  onEditDiscount,
  editable = false,
  busy = false,
  onAddService,
  onRemoveService,
  onChangeQty,
  onChangeCoverage,
  onChangeServiceDate,
  onChangeAuthCode,
  onChangeValidity,
  orderLines,
  onRevertOrderLine,
  onKeepOrderLine,
}: Props) {
  const selfPay = isSelfPayType(sponsorType);
  const insurance = isInsuranceType(sponsorType);
  // Column visibility (Flutter `invoice_services_widget.dart:294-333`):
  //   Sponsor Share + Auth Code + Validity Period → insurance only.
  //   Coverage + Service Date → any non-Self-pay (insurance OR corporate).
  const showSponsorShare = insurance;
  const showCoverage = !selfPay;
  const showServiceDate = !selfPay;
  const showAuth = insurance;
  const showValidity = insurance;

  const canRemove = editable && Boolean(onRemoveService);
  // Actions column shows when a row can have a remove (×, draft-only) and/or an
  // edit-discount pencil (Flutter shows the pencil beside the delete).
  const showActions = canRemove || Boolean(onEditDiscount);

  return (
    <section className="rounded-xl border border-border-default bg-surface-card px-6 py-6 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-text-primary">
          Services
        </h3>
        {editable && onAddService && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onAddService}
            disabled={busy}
          >
            <Plus className="h-4 w-4" aria-hidden /> Add service
          </Button>
        )}
      </div>

      {services.length === 0 ? (
        <p className="text-sm leading-5 text-text-muted py-4 text-center">
          No procedure added{editable ? '. Use the Add service button above.' : '.'}
        </p>
      ) : (
        // Wide services table (esp. insurance) — force an ALWAYS-VISIBLE styled
        // horizontal scrollbar so the row scrolls left/right on its own without
        // moving the page. `overflow-x:auto` alone gives an auto-hiding overlay
        // bar on Windows 11 (you "can't scroll right" with a plain mouse), so we
        // style ::-webkit-scrollbar to keep it persistent. Deliberate exception
        // to the scrollbar-hide rule (the table can exceed the viewport width).
        // The persistent scrollbar is drawn by `[data-billing="services-scroll"]` in src/styles/showcase-billing.css
        // (PM: arbitrary ::-webkit-scrollbar variants, which the PA guard forbids in className).
        <div data-billing="services-scroll" className="overflow-x-auto">
          <table className="w-max min-w-full text-left text-sm leading-5">
            <thead className="text-xs font-medium leading-4 text-text-secondary border-b border-border-default">
              <tr>
                <th className="px-3 py-2 font-medium w-8">#</th>
                <th className="px-3 py-2 font-medium">Services</th>
                <th className="px-3 py-2 font-medium text-right">Qty</th>
                <th className="px-3 py-2 font-medium text-right">Gross, {currency}</th>
                <th className="px-3 py-2 font-medium text-right">Disc.</th>
                <th className="px-3 py-2 font-medium text-right">Net, {currency}</th>
                <th className="px-3 py-2 font-medium text-right">Patient share</th>
                {showSponsorShare && (
                  <th className="px-3 py-2 font-medium text-right">Sponsor share</th>
                )}
                {showCoverage && <th className="px-3 py-2 font-medium">Coverage</th>}
                {showServiceDate && (
                  <th className="px-3 py-2 font-medium">Service date</th>
                )}
                {showAuth && <th className="px-3 py-2 font-medium">Auth code</th>}
                {showValidity && (
                  <th className="px-3 py-2 font-medium">Validity period</th>
                )}
                {showActions && <th className="px-3 py-2 w-16" aria-label="actions" />}
              </tr>
            </thead>
            <tbody>
              {services.map((s, i) => (
                <ServiceRow
                  key={s.id ?? i}
                  service={s}
                  index={i}
                  currency={currency}
                  editable={editable}
                  busy={busy}
                  selfPay={selfPay}
                  insurance={insurance}
                  showSponsorShare={showSponsorShare}
                  showCoverage={showCoverage}
                  showServiceDate={showServiceDate}
                  showAuth={showAuth}
                  showValidity={showValidity}
                  showActions={showActions}
                  canRemove={canRemove}
                  onEditDiscount={onEditDiscount}
                  onRemoveService={onRemoveService}
                  onChangeQty={onChangeQty}
                  onChangeCoverage={onChangeCoverage}
                  onChangeServiceDate={onChangeServiceDate}
                  onChangeAuthCode={onChangeAuthCode}
                  onChangeValidity={onChangeValidity}
                  orderInfo={s.sourceOrderKey ? orderLines?.[s.sourceOrderKey] : undefined}
                  onRevertOrderLine={onRevertOrderLine}
                  onKeepOrderLine={onKeepOrderLine}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface ServiceRowProps {
  service: InvoiceService;
  index: number;
  currency: string;
  editable: boolean;
  busy: boolean;
  selfPay: boolean;
  insurance: boolean;
  showSponsorShare: boolean;
  showCoverage: boolean;
  showServiceDate: boolean;
  showAuth: boolean;
  showValidity: boolean;
  showActions: boolean;
  canRemove: boolean;
  onEditDiscount?: (service: InvoiceService) => void;
  onRemoveService?: (serviceId: string) => void;
  onChangeQty?: (serviceId: string, quantity: number) => void;
  onChangeCoverage?: (serviceId: string, coverage: Coverage) => void;
  onChangeServiceDate?: (serviceId: string, yyyyMmDd: string) => void;
  onChangeAuthCode?: (serviceId: string, authCode: string) => void;
  onChangeValidity?: (
    serviceId: string,
    startYmd: string | undefined,
    endYmd: string | undefined,
  ) => void;
  orderInfo?: InvoiceServiceOrderInfo;
  onRevertOrderLine?: (sourceOrderKey: string) => void;
  onKeepOrderLine?: (sourceOrderKey: string) => void;
}

function ServiceRow({
  service: s,
  index: i,
  editable,
  busy,
  selfPay,
  insurance,
  showSponsorShare,
  showCoverage,
  showServiceDate,
  showAuth,
  showValidity,
  showActions,
  canRemove,
  onEditDiscount,
  onRemoveService,
  onChangeQty,
  onChangeCoverage,
  onChangeServiceDate,
  onChangeAuthCode,
  onChangeValidity,
  orderInfo,
  onRevertOrderLine,
  onKeepOrderLine,
}: ServiceRowProps) {
  const id = s.id ?? '';
  // Order to Invoice (PROD-2592): only lines added from an order.
  const fromOrder = isOrderLine(s);
  const flagged = fromOrder && needsKeepOrDrop(orderInfo);
  const canRevert = fromOrder && editable && Boolean(onRevertOrderLine);
  const coverage = inferCoverage(s);
  const lineName =
    s.procedureCode?.displayName ??
    s.procedureCode?.shortName ??
    s.shortDescription ??
    s.procedureCode?.code ??
    '—';

  // Auth code: local draft, commits on blur (parity with the quantity's
  // commit-on-blur — one recalc per edit, not one per keystroke).
  const initialAuth = s.authCode ?? s.authorizationCode ?? '';
  const [authDraft, setAuthDraft] = useState(initialAuth);
  useEffect(() => {
    setAuthDraft(initialAuth);
  }, [initialAuth]);

  const editCoverage = editable && showCoverage && Boolean(onChangeCoverage) && !!id;
  const editServiceDate =
    editable && showServiceDate && Boolean(onChangeServiceDate) && !!id;
  const editAuth = editable && showAuth && Boolean(onChangeAuthCode) && !!id;
  const editValidity = editable && showValidity && Boolean(onChangeValidity) && !!id;

  // Discount pencil: Self-pay → always (when `onEditDiscount` set); insurance /
  // corporate → only on Excluded rows (Flutter `:575-586`). Also gated on
  // `editable` (Draft) so a non-Draft caller can never surface it (PROD-1759).
  const canDiscount =
    editable && Boolean(onEditDiscount) && (selfPay || coverage === COVERAGE_EXCLUDED);

  const memberChip = s.memberPriceApplied ? memberChipOf(s) : null;

  return (
    <tr
      className={`border-b border-border-subtle last:border-0 align-middle${
        flagged ? ' bg-yellow-100/60' : ''
      }`}
    >
      <td className="px-3 py-2 text-text-muted">{i + 1}</td>
      <td className="px-3 py-2 text-text-primary">
        <div className="font-medium">{lineName}</div>
        {/* Code + coding system + category — the fields that identify what was
            charged (Flutter invoice_services_widget.dart:343-347). Read the flat
            wire fields billingGroupSystem / billingGroupCategory, not a nested
            billingGroup that isn't on the wire (PROD-1757). The system is the
            Service's own (procedureCode.system), not its billing group's: an
            Internal service in a CPT group read as CPT (PROD-3156). The
            billing group's system is the fallback for a service without one. */}
        {(s.procedureCode?.code || s.procedureCode?.system || s.billingGroupSystem) && (
          <div className="text-xs leading-5 text-text-muted tabular-nums">
            {[s.procedureCode?.code, s.procedureCode?.system || s.billingGroupSystem]
              .filter(Boolean)
              .join(' • ')}
          </div>
        )}
        {s.billingGroupCategory && (
          <div className="text-xs leading-5 text-text-muted">{s.billingGroupCategory}</div>
        )}
        {/* Why this line costs what it costs (PROD-2152). Without it, a line
            repriced from a 300 location fee to a member's 150 is identical on
            screen to one discounted by 150 — the membership looks like it did
            nothing, and "why is this line 150?" has no answer at the desk.
            The chip carries the SAVING rather than the fee it replaced: earlier
            wordings put a second price ("list 300.00", then "normally 300.00")
            next to the two already on the row, and a reader who knows this
            domain read the 300 as the member price. "saves 150.00" is the one
            number the row does not already show and cannot be mistaken for the
            one charged. Billing records the inputs on the line at pricing time,
            so this reports what was actually charged rather than re-deriving it
            from a plan that may since have been repriced. Same badge as the
            settlement strip and the patient's receipts list.

            PROD-2523 adds the percentage that saving works out to — "saves
            30.00 (10% off)". A plan-level percentage benefit is sold as a
            percentage ("members get 10% off"), so that is the form a member can
            match against a line; the amount alone leaves them doing the
            division. It goes INSIDE the existing chip, on the rows that already
            earned one, rather than becoming a badge on every row or a twelfth
            column — this table already runs to eleven depending on sponsor type,
            and the rows worth explaining are the surprising ones.

            PROD-2525: Billing now records WHICH rung priced the line, so the
            chip says "Member discount 10%" for the plan percentage and "Member
            price" for an explicit member price — see `memberChipOf`, including
            why a line without the record keeps the PROD-2523 wording. */}
        {memberChip && (
          <div className={MEMBER_PRICE_CHIP_CLASS}>
            <BadgeCheck className="h-3 w-3" aria-hidden />
            <span>{memberChip.label}</span>
            {memberChip.savingText !== null && (
              <span className="tabular-nums">{memberChip.savingText}</span>
            )}
          </div>
        )}
        {fromOrder && <OrderSourceMarker service={s} info={orderInfo} />}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {editable && onChangeQty && id ? (
          <QtyCell
            value={s.quantity ?? 1}
            disabled={busy}
            onCommit={(q) => onChangeQty(id, q)}
          />
        ) : (
          s.quantity ?? 1
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{fmt(s.grossAmount ?? s.fee)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-text-muted">
        {fmt(s.discountAmount ?? s.discount)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{fmt(s.netAmount)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {fmt(s.patientShare ?? s.patientAmount)}
      </td>
      {showSponsorShare && (
        <td className="px-3 py-2 text-right tabular-nums">{fmt(s.sponsorAmount)}</td>
      )}
      {showCoverage && (
        <td className="px-3 py-2">
          {editCoverage ? (
            <Select
              value={coverage}
              onValueChange={(v) => onChangeCoverage!(id, v as Coverage)}
              disabled={busy}
            >
              <SelectTrigger className="h-8 min-w-36 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={COVERAGE_INCLUDED}>{COVERAGE_INCLUDED}</SelectItem>
                <SelectItem value={COVERAGE_EXCLUDED}>{COVERAGE_EXCLUDED}</SelectItem>
                {/* "Needs Approval" is an insurance-only option (Flutter
                    `:97-106`); corporate gets Included / Excluded only. */}
                {insurance && (
                  <SelectItem value={COVERAGE_NEEDS_APPROVAL}>
                    {COVERAGE_NEEDS_APPROVAL}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-text-secondary">{coverage}</span>
          )}
        </td>
      )}
      {showServiceDate && (
        <td className="px-3 py-2">
          {editServiceDate ? (
            <ServiceDatePicker
              value={s.serviceAddedDate}
              onChange={(v) => onChangeServiceDate!(id, v)}
              disabled={busy}
            />
          ) : (
            <span className="text-text-secondary">
              {formatServiceDate(s.serviceAddedDate)}
            </span>
          )}
        </td>
      )}
      {showAuth && (
        <td className="px-3 py-2">
          {editAuth ? (
            <input
              type="text"
              value={authDraft}
              disabled={busy}
              onChange={(e) => setAuthDraft(e.target.value)}
              onBlur={() => {
                if (authDraft !== initialAuth) onChangeAuthCode!(id, authDraft);
              }}
              placeholder="—"
              className="w-24 border border-border-default rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
            />
          ) : (
            <span className="text-text-secondary">
              {s.authCode ?? s.authorizationCode ?? '—'}
            </span>
          )}
        </td>
      )}
      {showValidity && (
        <td className="px-3 py-2">
          {editValidity ? (
            <DateRangeField
              startDate={toYmd(s.validityStartDate)}
              endDate={toYmd(s.validityEndDate)}
              // Guard: the range field commits on close — skip the API call when
              // the popover closed with no actual change (e.g. opened + closed).
              onChange={(start, end) => {
                if (
                  (start ?? '') === toYmd(s.validityStartDate) &&
                  (end ?? '') === toYmd(s.validityEndDate)
                ) {
                  return;
                }
                onChangeValidity!(id, start, end);
              }}
              commitOn="close"
              disabled={busy}
              placeholder="Validity period"
              className="min-w-42"
            />
          ) : (
            <span className="text-text-secondary">
              {fmtValidityDisplay(s.validityStartDate, s.validityEndDate)}
            </span>
          )}
        </td>
      )}
      {showActions && (
        <td className="px-3 py-2">
          {flagged && canRevert && onKeepOrderLine ? (
            <RemovedOrderActions
              sourceOrderKey={s.sourceOrderKey!}
              display={lineName}
              busy={busy}
              onKeep={onKeepOrderLine}
              onDrop={onRevertOrderLine!}
            />
          ) : (
            <div className="flex items-center justify-end gap-2">
              {canRevert && (
                <RevertToPendingButton
                  sourceOrderKey={s.sourceOrderKey!}
                  display={lineName}
                  busy={busy}
                  onRevert={onRevertOrderLine!}
                />
              )}
              {canRemove && onRemoveService && id && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        aria-label="Remove service"
                        onClick={() => onRemoveService(id)}
                        disabled={busy}
                        className={`${ROW_ICON_BUTTON} text-status-error`}
                      />
                    }
                  >
                    <X className="size-4" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent>Remove service</TooltipContent>
                </Tooltip>
              )}
              {canDiscount && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        aria-label="Edit service discount"
                        onClick={() => onEditDiscount!(s)}
                        disabled={busy}
                        className={`${ROW_ICON_BUTTON} text-text-secondary`}
                      />
                    }
                  >
                    <Pencil className="size-4" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent>Edit service discount</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * Quantity input — commits on blur / Enter (not per keystroke) so each edit is
 * one recalc round-trip, not one-per-digit. Resets to the committed value on
 * empty / invalid input.
 */
function QtyCell({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled?: boolean;
  onCommit: (quantity: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    const n = Math.floor(Number(trimmed));
    if (trimmed === '' || !Number.isFinite(n) || n < 1) {
      setDraft(String(value));
      return;
    }
    if (n !== value) onCommit(n);
    else setDraft(String(n));
  };

  return (
    <input
      type="number"
      min="1"
      step="1"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="w-14 text-right border border-border-default rounded-md px-2 py-1 text-sm tabular-nums bg-grey-100 focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
      // No spin buttons: `[data-billing="qty-input"]` in src/styles/showcase-billing.css (PM: arbitrary variants).
      data-billing="qty-input"
    />
  );
}
